import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ShoppingBag, ArrowLeft } from 'lucide-react';

const onlineStoresByCity = {
  nicosia: {
    name: 'Nicosia',
    nameGr: 'Λευκωσία',
    stores: [
      {
        id: 1,
        name: 'Wolt Market Nicosia Central',
        description: 'Fast delivery to central Nicosia'
      },
      {
        id: 2,
        name: 'Wolt Market Parissinos',
        description: 'Delivery from Parissinos area'
      }
    ]
  },
  larnaca: {
    name: 'Larnaca',
    nameGr: 'Λάρνακα',
    stores: [
      {
        id: 1,
        name: 'Wolt Market Larnaca',
        description: 'Fast delivery in Larnaca'
      }
    ]
  },
  limassol: {
    name: 'Limassol',
    nameGr: 'Λεμεσός',
    stores: [
      {
        id: 1,
        name: 'Wolt Market Agios Athanasios',
        description: 'Delivery from Agios Athanasios area'
      },
      {
        id: 2,
        name: 'Wolt Market Limassol West',
        description: 'Delivery to west Limassol'
      }
    ]
  },
  paphos: {
    name: 'Paphos',
    nameGr: 'Πάφος',
    stores: [
      {
        id: 1,
        name: 'Wolt Market Paphos',
        description: 'Fast delivery in Paphos'
      }
    ]
  },
  ammochostos: {
    name: 'Ammochostos',
    nameGr: 'Αμμόχωστος',
    stores: [
      {
        id: 1,
        name: 'Wolt Market Famagusta',
        description: 'Fast delivery in Famagusta area'
      }
    ]
  }
};

export default function OnlineStores() {
  const { city } = useParams();
  const cityData = onlineStoresByCity[city];

  useEffect(() => {
    document.title = `Shop Online in ${cityData?.name || 'City'} - Salt & Stone`;
  }, [cityData]);

  if (!cityData) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-[#F2F0E9] text-2xl mb-4">City not found</h1>
          <Link to="/find" className="text-[#D4AF37] hover:underline">
            Back to regions
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="online-stores-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-start justify-center overflow-hidden bg-black pt-24">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="lg:hidden absolute left-1/2 -translate-x-1/2 top-0 -translate-y-[40%] h-[95vh] w-[90vw]">
            <img
              src="https://customer-assets.emergentagent.com/job_60caa5be-1d50-4b72-801b-3659f641dade/artifacts/9uzc6zo8_IMG_7292.jpg"
              alt="Salt & Stone Gin Bottle"
              className="h-full w-full object-contain opacity-30"
            />
            <div className="absolute inset-0 pointer-events-none" style={{
              background: 'radial-gradient(ellipse 80% 70% at center 40%, transparent 45%, #000000 85%)'
            }}></div>
          </div>
          <img
            src="https://customer-assets.emergentagent.com/job_60caa5be-1d50-4b72-801b-3659f641dade/artifacts/9uzc6zo8_IMG_7292.jpg"
            alt="Salt & Stone Gin Bottle"
            className="hidden lg:block absolute right-0 top-1/2 -translate-y-1/2 h-full w-auto max-w-none object-contain opacity-50 translate-x-[20%]"
          />
          <div className="hidden lg:block absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/80 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/50 via-transparent to-[#050505]"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 w-full max-w-4xl mx-auto px-6 py-12">
          {/* Back Button */}
          <Link 
            to={`/find/${city}`}
            className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#F2F0E9] transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Link>

          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <ShoppingBag className="w-6 h-6 text-[#D4AF37]" />
              <span className="caption">Shop Online</span>
            </div>
            <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl text-[#F2F0E9] mb-4">
              {cityData.name}
            </h1>
            <p className="text-[#F2F0E9]/60">
              Order Salt & Stone online for delivery
            </p>
          </div>

          {/* Online Stores List */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {cityData.stores.map((store) => (
              <div 
                key={store.id}
                className="border border-white/10 bg-black/40 backdrop-blur-sm p-6 hover:border-[#D4AF37]/30 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <ShoppingBag className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-['Playfair_Display'] text-xl text-[#F2F0E9] mb-2">
                      {store.name}
                    </h3>
                    {store.description && (
                      <p className="text-[#F2F0E9]/60 text-sm">
                        {store.description}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Also available section */}
          <div className="mt-12 text-center border-t border-white/10 pt-12">
            <p className="text-[#F2F0E9]/60 mb-4">Also available on our website</p>
            <Link 
              to="/shop" 
              className="btn-primary inline-flex items-center gap-2"
            >
              <ShoppingBag className="w-4 h-4" />
              Shop on Salt & Stone
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
